import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, ChevronLeft, ChevronRight, Compass, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import tourSteps from "./tourSteps";
import "./AppTourModal.css";

const TARGET_PADDING = 12;
const TARGET_RETRY_MS = 100;
const TARGET_RETRY_LIMIT = 45;
const CARD_WIDTH = 408;
const CARD_GAP = 20;
const CARD_HEIGHT = 380;

const AppTourModal = ({ onClose }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [viewport, setViewport] = useState(() => getViewportSize());
  const [targetRect, setTargetRect] = useState(null);
  const [isLocating, setIsLocating] = useState(true);
  const [hasTarget, setHasTarget] = useState(false);
  const targetElRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const retryTimerRef = useRef(null);
  const measureTimerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const step = tourSteps[stepIndex];
  const selector = useMemo(() => `[data-tour="${step.target}"]`, [step.target]);
  const placement = useMemo(
    () => getCardPlacement(targetRect, viewport, step.placement),
    [step.placement, targetRect, viewport]
  );

  const finishTour = useCallback(() => {
    localStorage.setItem("appTourShown", "true");
    onClose();
  }, [onClose]);

  const next = useCallback(() => {
    if (stepIndex === tourSteps.length - 1) {
      finishTour();
      return;
    }

    setStepIndex((prev) => prev + 1);
  }, [finishTour, stepIndex]);

  const prev = useCallback(() => {
    setStepIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const measureTarget = useCallback(() => {
    const element = targetElRef.current;

    if (!element) {
      setTargetRect(null);
      return false;
    }

    const rect = element.getBoundingClientRect();
    const left = Math.max(rect.left - TARGET_PADDING, 12);
    const top = Math.max(rect.top - TARGET_PADDING, 12);
    const right = Math.min(rect.right + TARGET_PADDING, window.innerWidth - 12);
    const bottom = Math.min(rect.bottom + TARGET_PADDING, window.innerHeight - 12);
    setTargetRect({
      top,
      left,
      width: Math.max(0, right - left),
      height: Math.max(0, bottom - top),
      right,
      bottom,
      centerX: rect.left + rect.width / 2,
      centerY: rect.top + rect.height / 2,
    });
    return true;
  }, []);

  useEffect(() => {
    document.body.classList.add("app-tour-active");
    return () => document.body.classList.remove("app-tour-active");
  }, []);

  useEffect(() => {
    const onResize = () => setViewport(getViewportSize());
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  useEffect(() => {
    if (step.expandProducts) {
      window.dispatchEvent(new Event("appTour:openProducts"));
    }

    if (location.pathname !== step.route) {
      navigate(step.route, { replace: false });
    }
  }, [location.pathname, navigate, step]);

  useEffect(() => {
    setIsLocating(true);
    setHasTarget(false);
    setTargetRect(null);
    targetElRef.current = null;

    if (measureTimerRef.current) {
      window.clearTimeout(measureTimerRef.current);
      measureTimerRef.current = null;
    }

    if (retryTimerRef.current) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }

    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }

    const findTarget = (attempt = 0) => {
      const element = document.querySelector(selector);

      if (!element) {
        if (attempt < TARGET_RETRY_LIMIT) {
          retryTimerRef.current = window.setTimeout(() => findTarget(attempt + 1), TARGET_RETRY_MS);
        } else {
          setIsLocating(false);
        }
        return;
      }

      targetElRef.current = element;
      setHasTarget(true);
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });

      measureTimerRef.current = window.setTimeout(() => {
        measureTarget();
        setIsLocating(false);
      }, 180);

      if (typeof ResizeObserver !== "undefined") {
        resizeObserverRef.current = new ResizeObserver(() => {
          measureTarget();
        });
        resizeObserverRef.current.observe(element);
      }
    };

    findTarget();

    return () => {
      if (measureTimerRef.current) {
        window.clearTimeout(measureTimerRef.current);
      }
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [measureTarget, selector, stepIndex, location.pathname]);

  useEffect(() => {
    if (!hasTarget) return undefined;

    const updateOnScroll = () => measureTarget();
    window.addEventListener("scroll", updateOnScroll, true);
    window.addEventListener("resize", updateOnScroll);

    return () => {
      window.removeEventListener("scroll", updateOnScroll, true);
      window.removeEventListener("resize", updateOnScroll);
    };
  }, [hasTarget, measureTarget]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") finishTour();
      if (event.key === "ArrowRight") next();
      if (event.key === "ArrowLeft") prev();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [finishTour, next, prev]);

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className="app-tour" role="dialog" aria-modal="true" aria-labelledby="app-tour-title" aria-live="polite">
      {targetRect ? (
        <>
          <div className="app-tour__backdrop" style={{ top: 0, left: 0, width: viewport.width, height: targetRect.top }} />
          <div className="app-tour__backdrop" style={{ top: targetRect.bottom, left: 0, width: viewport.width, height: Math.max(0, viewport.height - targetRect.bottom) }} />
          <div className="app-tour__backdrop" style={{ top: targetRect.top, left: 0, width: targetRect.left, height: targetRect.height }} />
          <div className="app-tour__backdrop" style={{ top: targetRect.top, left: targetRect.right, width: Math.max(0, viewport.width - targetRect.right), height: targetRect.height }} />
        </>
      ) : (
        <div className="app-tour__backdrop" />
      )}

      {targetRect && (
        <div
          className="app-tour__focus"
          data-placement={placement.name}
          style={getFocusStyle(targetRect)}
          aria-hidden="true"
        />
      )}

      <section
        className="app-tour__card"
        data-placement={placement.name}
        style={getCardStyle(placement, viewport, isLocating)}
      >
        <header className="app-tour__header">
          <div className="app-tour__brand">
            <div className="app-tour__brand-icon">
              <Compass size={15} />
            </div>
            <div className="app-tour__brand-copy">
              <span className="app-tour__brand-name">Product tour</span>
              <span className="app-tour__brand-subtitle">{step.surface}</span>
            </div>
          </div>

          <button className="app-tour__close" type="button" onClick={finishTour} aria-label="Close app tour">
            <X size={17} />
          </button>
        </header>

        <div className="app-tour__meta">
          <span className="app-tour__step-pill">{step.eyebrow}</span>
          <span className="app-tour__count">
            Step {stepIndex + 1} of {tourSteps.length}
          </span>
        </div>

        <div className="app-tour__segments" aria-hidden="true">
          {tourSteps.map((tourStep, index) => (
            <span
              key={tourStep.id}
              className={[
                "app-tour__segment",
                index < stepIndex ? "is-complete" : "",
                index === stepIndex ? "is-active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            />
          ))}
        </div>

        <div className="app-tour__body">
          <div className="app-tour__headline">
            <h2 id="app-tour-title">{step.title}</h2>
            <p>{step.desc}</p>
          </div>

          <div className="app-tour__hint">
            <CheckCircle2 size={18} />
            <span>{step.hint}</span>
          </div>

          {isLocating && (
            <div className="app-tour__status">
              <span className="app-tour__spinner" aria-hidden="true" />
              <span>Locating this section on the page.</span>
            </div>
          )}
          {!isLocating && !hasTarget && (
            <div className="app-tour__status app-tour__status--missing" role="status">
              <span>Couldn’t find this section. You can continue the tour or revisit this page later.</span>
            </div>
          )}
        </div>

        <footer className="app-tour__footer">
          <button className="app-tour__link" type="button" onClick={finishTour}>
            Skip tour
          </button>

          <div className="app-tour__actions">
            <button className="app-tour__secondary" type="button" onClick={prev} disabled={stepIndex === 0}>
              <ChevronLeft size={17} />
              Back
            </button>

            <button className="app-tour__primary" type="button" onClick={next}>
              {step.cta}
              <ChevronRight size={17} />
            </button>
          </div>
        </footer>
      </section>
    </div>,
    document.body
  );
};

const getViewportSize = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

const getFocusStyle = (rect) => ({
  top: rect.top,
  left: rect.left,
  width: rect.width,
  height: rect.height,
});

const getCardStyle = (placement, viewport, isLocating) => {
  const width = Math.min(CARD_WIDTH, viewport.width - 24);
  const maxHeight = placement.maxHeight || Math.min(viewport.height - 24, CARD_HEIGHT);

  if (isLocating || !placement.name) {
    return {
      width,
      maxHeight,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    };
  }

  const { left, top, transform } = placement;
  return {
    width,
    maxHeight,
    top,
    left,
    transform,
  };
};

const getCardPlacement = (rect, viewport, preferredPlacement) => {
  if (!rect) {
    return { name: "", top: 0, left: 0, transform: "" };
  }

  const width = Math.min(CARD_WIDTH, viewport.width - 24);
  const height = Math.min(CARD_HEIGHT, viewport.height - 24);
  const centerX = rect.centerX ?? rect.left + rect.width / 2;
  const centerY = rect.centerY ?? rect.top + rect.height / 2;
  const spaceRight = viewport.width - (rect.right + CARD_GAP);
  const spaceLeft = rect.left - CARD_GAP;
  const spaceBelow = viewport.height - (rect.bottom + CARD_GAP);
  const spaceAbove = rect.top - CARD_GAP;
  const wideTarget = rect.width >= viewport.width * 0.52;
  const available = {
    right: spaceRight >= width,
    left: spaceLeft >= width,
    below: spaceBelow >= height,
    above: spaceAbove >= height,
  };

  let name = "right";
  if (preferredPlacement && available[preferredPlacement]) {
    name = preferredPlacement;
  } else if (wideTarget) {
    name = spaceBelow >= spaceAbove ? "below" : "above";
  } else if (spaceRight >= width) {
    name = "right";
  } else if (spaceLeft >= width) {
    name = "left";
  } else {
    name = spaceBelow >= spaceAbove ? "below" : "above";
  }

  const clampX = (x) => Math.max(12 + width / 2, Math.min(x, viewport.width - 12 - width / 2));
  const clampCenterY = (y) => Math.max(12 + height / 2, Math.min(y, viewport.height - 12 - height / 2));
  if (name === "right") {
    return {
      name,
      maxHeight: height,
      top: clampCenterY(centerY),
      left: Math.min(rect.right + CARD_GAP, viewport.width - width - 12),
      transform: "translateY(-50%)",
    };
  }

  if (name === "left") {
    return {
      name,
      maxHeight: height,
      top: clampCenterY(centerY),
      left: Math.max(rect.left - CARD_GAP, 12 + width),
      transform: "translate(-100%, -50%)",
    };
  }

  if (name === "above") {
    return {
      name,
      maxHeight: height,
      top: Math.max(rect.top - CARD_GAP, 12 + height),
      left: clampX(centerX),
      transform: "translate(-50%, -100%)",
    };
  }

  return {
    name: "below",
    maxHeight: height,
    top: rect.bottom + CARD_GAP,
    left: clampX(centerX),
    transform: "translateX(-50%)",
  };
};

export default AppTourModal;
